import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Paper,
  Toolbar,
  Typography,
  AppBar,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import SettingsIcon from '@mui/icons-material/Settings';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { TableSkeleton } from '../../../components/TableSkeleton';
import CustomTextField from '../../../components/Textfield';
import useCenterUsers from '../../../hooks/useCenterUsers';
import useUser from '../../../hooks/useUser';
import CenterStudentManagement from './CenterStudentManagement';

const drawerWidth = 240;

const openedMixin = () => ({
  width: drawerWidth,
  backgroundImage: 'linear-gradient(to right, #454751d1 0%, #454751d1 100%)',
  color: '#fff',
  overflowX: 'hidden',
});

const closedMixin = () => ({
  width: '60px',
  backgroundImage: 'linear-gradient(to right, #454751d1 0%, #454751d1 100%)',
  color: '#fff',
  overflowX: 'hidden',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

function CenterDashboardHome() {
  const { user } = useSelector((state) => state.user);
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 4, mt: 2, borderRadius: 3, background: 'linear-gradient(135deg, #f5f5f5 0%, #fff 100%)' }}
      >
        <Typography variant="h5" fontWeight={600} sx={{ color: '#454751d1', mb: 1 }}>
          Welcome to The Accounts Express Portal
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Use the sidebar to manage student registrations or update your profile settings.
        </Typography>
      </Paper>
    </Box>
  );
}

function CenterSettings() {
  const { getCenterProfile, updateProfile, isUpdatingProfile } = useCenterUsers();
  const [profileLoading, setProfileLoading] = useState(true);

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    getCenterProfile()
      .then((data) => {
        if (data?.user) {
          reset({
            firstName: data.user.firstName || '',
            lastName: data.user.lastName || '',
            phoneNumber: data.user.phoneNumber || '',
            centerName: data.user.centerName || '',
            address: data.user.address || '',
          });
        }
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setProfileLoading(false));
  }, []); // eslint-disable-line

  const handleSave = async (values) => {
    try {
      await updateProfile(values);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  if (profileLoading) return <TableSkeleton />;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      <Paper variant="outlined" sx={{ p: 4, mt: 2, borderRadius: 3, maxWidth: 600 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Profile Information
        </Typography>
        <form onSubmit={handleSubmit(handleSave)}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                {...register('firstName', { required: 'First name is required' })}
                fullWidth
                label="First Name*"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <CustomTextField
                {...register('lastName', { required: 'Last name is required' })}
                fullWidth
                label="Last Name*"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <CustomTextField
                {...register('phoneNumber')}
                fullWidth
                label="Phone Number"
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <CustomTextField
                {...register('centerName')}
                fullWidth
                label="Center Name"
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <CustomTextField
                {...register('address')}
                fullWidth
                label="Address"
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdatingProfile}
                sx={{ background: '#454751d1', fontSize: 14, mt: 1 }}
              >
                {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default function CenterDashboard() {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState('Home');
  const [openLogoutDialog, setLogoutDialog] = useState(false);
  const { userLogoutMutation, isLoggingOut } = useUser();

  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      setOpen(!isMobileView);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleLogout = async () => {
    try {
      await userLogoutMutation();
      setLogoutDialog(false);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong');
    }
  };

  const renderSelectedComponent = () => {
    switch (selectedComponent) {
      case 'Registration':
        return <CenterStudentManagement />;
      case 'Settings':
        return <CenterSettings />;
      default:
        return <CenterDashboardHome />;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundImage: 'linear-gradient(to right, #454751d1 0%, #ebb57dc4 100%)',
          color: '#fff',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" onClick={() => setOpen(true)} edge="start" sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap>
            Center Portal
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="large" edge="end" onClick={() => setLogoutDialog(true)} color="inherit">
            <LogoutIcon sx={{ fontSize: 25 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={isMobile ? () => setOpen(false) : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': open ? openedMixin() : closedMixin(),
        }}
      >
        <DrawerHeader>
          <Typography variant="h6" sx={{ pl: 2 }}>
            Menu
          </Typography>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />

        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setSelectedComponent('Home')}>
              <ListItemIcon sx={{ color: '#fff' }}>
                <DashboardIcon fontSize="large" />
              </ListItemIcon>
              <Typography fontSize={15}>Dashboard</Typography>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => setSelectedComponent('Registration')}>
              <ListItemIcon sx={{ color: '#fff' }}>
                <PermContactCalendarIcon fontSize="large" />
              </ListItemIcon>
              <Typography fontSize={15}>Student Registration</Typography>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => setSelectedComponent('Settings')}>
              <ListItemIcon sx={{ color: '#fff' }}>
                <SettingsIcon fontSize="large" />
              </ListItemIcon>
              <Typography fontSize={15}>Settings</Typography>
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        {renderSelectedComponent()}
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog open={openLogoutDialog} onClose={() => setLogoutDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Logout
            <IconButton edge="end" color="inherit" onClick={() => setLogoutDialog(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 3 }}>
          Are you sure you want to logout?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)} sx={{ fontSize: 13, color: '#454751d1' }}>
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            sx={{ fontSize: 13 }}
            variant="contained"
            disabled={isLoggingOut}
            color="error"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
